// ============================================
// components/auth/Login.js
// ============================================
import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  AppBar, Toolbar, Typography, Container, TextField, 
  Button, Grid, Paper, Box, CssBaseline, InputAdornment, IconButton, Link as MuiLink, Stack
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import theme from '../../styles/theme';
import { isValidEmail } from '../../utils/helpers';
import { authService } from '../../services/authService';
import { ROUTES } from '../../utils/constants';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Detect Cypress-simulated expired session and inform the user
  useEffect(() => {
    try {
      const token = window.localStorage.getItem('authToken');
      if (token === 'expired-token') {
        setError('Session expired');
        window.localStorage.removeItem('authToken');
      }
    } catch (_) {}
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Email and password are required");
      return;
    }

    // Use shared strict email validator for consistency
    if (!isValidEmail(email)) {
      setError('Invalid email format');
      return;
    }

    setLoading(true);
    const result = await authService.login(email, password);
    setLoading(false);

    if (result.success) {
      setError(null);
      navigate(ROUTES.HOME);
    } else {
      setError(result.error);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar component="header" position="sticky" color="transparent" elevation={0} sx={{
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(17, 24, 39, 0.4)',
        borderBottom: '1px solid rgba(255,255,255,0.06)'
      }}>
        <Toolbar variant="dense" sx={{ minHeight: 48, px: 2 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Porchlight
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button color="inherit" component={RouterLink} to={ROUTES.SIGNUP}>
            Sign Up
          </Button>
        </Toolbar>
      </AppBar>
      <Container component="main" aria-labelledby="login-page-title" maxWidth="xs" sx={{ px: 2 }}>
        {/* Visually hidden page heading for accessibility (inside main landmark) */}
        <Box component="h1" id="login-page-title" sx={{
          border: 0,
          clip: 'rect(0 0 0 0)',
          height: 1,
          margin: -1,
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: 1
        }}>
          Login - Porchlight
        </Box>
        <Paper elevation={0} variant="glass" sx={{
          mt: 8,
          px: 3,
          py: 3
        }}>
          <Stack spacing={2} alignItems="stretch">
            <Typography variant="h5" component="h2" align="center" sx={{ fontWeight: 700 }}>
              Welcome back
            </Typography>
            <Typography id="login-form-title" variant="h6" component="h3" align="center" sx={{ fontWeight: 600 }}>
              Login
            </Typography>
          </Stack>
          <Box component="form" aria-labelledby="login-form-title" noValidate onSubmit={handleLogin} sx={{ mt: 2 }} data-testid="login-form">
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              type="email"
              inputMode="email"
              inputProps={{ 'data-testid': 'login-email' }}
              InputLabelProps={{ sx: { color: 'text.primary' } }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              inputProps={{ 'data-testid': 'login-password' }}
              InputLabelProps={{ sx: { color: 'text.primary' } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && <Typography color="error">{error}</Typography>}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <MuiLink component={RouterLink} to={ROUTES.FORGOT_PASSWORD} variant="body2" sx={{ alignSelf: 'center' }}>
                Forgot your password?
              </MuiLink>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                component={RouterLink}
                to={ROUTES.SIGNUP}
              >
                Create an account
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default Login;

